import {
  createContext,
  type EventHandler,
  type FormEvent,
  type ReactElement,
  type ReactNode,
  useContext,
  useId,
} from "react";
import { twMerge } from "tailwind-merge";
import { Badge, BadgeBlue } from "../Badge";
import { H5, H6 } from "../Header";

type RadioSelectorContextValue = {
  onChange?: (e: FormEvent<HTMLInputElement>) => void;
  name: string;
  value: string;
  required?: boolean;
  variant: RadioSelectorVariant;
};

const RadioSelectorContext = createContext<RadioSelectorContextValue | null>(
  null,
);

const useRadioSelectorContext = (): RadioSelectorContextValue => {
  const context = useContext(RadioSelectorContext);

  if (!context) {
    throw new Error("RadioSelector options must be used within RadioSelector");
  }

  return context;
};

type RadioSelectorVariant = "stacked" | "grouped";

export type RadioSelectorProps = {
  value: string;
  onChange?: EventHandler<FormEvent<HTMLInputElement>>;
  children: ReactNode;
  required?: boolean;
  variant?: RadioSelectorVariant;
};

export const RadioSelector = ({
  value,
  onChange,
  children,
  required,
  variant = "stacked",
}: RadioSelectorProps): ReactElement => {
  const name = useId();

  return (
    <RadioSelectorContext.Provider
      value={{
        name,
        value,
        onChange,
        required,
        variant,
      }}
    >
      <div>{children}</div>
    </RadioSelectorContext.Provider>
  );
};

export type RadioSelectorOptionProps = {
  value: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  selected?: boolean;
  variant?: RadioSelectorVariant;
};

export const RadioSelectorOption = ({
  value,
  children,
  disabled,
  className,
  selected,
  variant,
}: RadioSelectorOptionProps): ReactElement => {
  const context = useRadioSelectorContext();
  const checked = selected ?? context.value === value;
  const resolvedVariant = variant ?? context.variant ?? "stacked";

  return (
    <div
      className={twMerge(
        resolvedVariant === "stacked" &&
          "mt-2 rounded-lg border border-solid border-zinc-300 bg-white p-4 first:mt-0 dark:border-zinc-800 dark:bg-zinc-900",
        resolvedVariant === "grouped" &&
          "border border-solid border-zinc-300 bg-white p-4 first:rounded-t-lg last:rounded-b-lg dark:border-zinc-800 dark:bg-zinc-900",
        checked &&
          resolvedVariant === "stacked" &&
          "border border-solid border-black dark:border-zinc-100",
        checked &&
          resolvedVariant === "grouped" &&
          "dark:bg-primary-950 border-primary-400 bg-primary-50 dark:border-primary-400 border border-solid",
        disabled && "bg-zinc-50 dark:bg-zinc-950",
        className,
      )}
    >
      <label
        htmlFor={value}
        className="flex cursor-pointer items-baseline gap-2"
      >
        <input
          type="radio"
          className="bg-primary-500"
          onChange={context.onChange}
          name={context.name}
          id={value}
          value={value}
          checked={checked}
          disabled={disabled}
          required={context.required}
        />
        <div className="w-full">{children}</div>
      </label>
    </div>
  );
};

export type RadioSelectorItemProps = {
  value: string;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  recommended?: boolean;
  className?: string;
};

export const RadioSelectorItem = ({
  value,
  title,
  description,
  children,
  disabled = false,
  recommended = false,
  className,
}: RadioSelectorItemProps): ReactElement => (
  <RadioSelectorOption value={value} disabled={disabled} className={className}>
    {title && (
      <div className="flex flex-row justify-between">
        <H5>{title}</H5>
        {recommended &&
          (disabled ? (
            <Badge>Recommended</Badge>
          ) : (
            <BadgeBlue>Recommended</BadgeBlue>
          ))}
      </div>
    )}
    {description && <H6>{description}</H6>}
    {children}
  </RadioSelectorOption>
);
